import { isLotCancelled } from './LotUtil';

/**
 * Get the style object for a lot card based on its status and user interaction
 * @param {Object} lot - The lot object
 * @param {Object} options - Configuration options
 * @param {Object} options.currentUser - Current logged in user
 * @param {number} options.auctionOwnerId - ID of the auction owner
 * @param {boolean} options.isSelected - Whether the lot is currently selected
 * @param {Set} options.newBidLots - Set of lot IDs that have new bids (for animation)
 * @param {boolean} options.isAvailable - Whether the lot is available for bidding
 * @returns {Object} Style object for the lot card
 */
export const getLotCardStyle = (lot, options = {}) => {
    const {
        currentUser = null,
        auctionOwnerId = null,
        isSelected = false,
        newBidLots = null,
        isAvailable = true
    } = options;

    let style = {
        height: '160px',
        border: '2px solid #e0e0e0',
        width: '100%',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden'
    };
    
    // Check if lot is effectively cancelled (closed without reaching reserve)
    const isCancelled = isLotCancelled(lot);
    
    if (isCancelled) {
        style = { 
            ...style, 
            border: '2px solid #722ed1',
            background: '#f9f0ff',
            opacity: 0.8
        };
    } else if (lot.status === 'timeout') {
        style = { 
            ...style, 
            border: '2px solid #fa8c16',
            background: '#fff7e6',
            opacity: 0.8
        };
    } else if (lot.status === 'closed') {
        if (currentUser?.id === auctionOwnerId) {
            style = { ...style, border: '2px solid #1890ff', background: '#e6f7ff' };
        } else if (lot.last_bid_user_id === currentUser?.id) {
            style = { ...style, border: '2px solid #52c41a', background: '#f6ffed' };
        } else if (lot.last_bid_user_id) {
            style = { ...style, border: '2px solid #ff4d4f', background: '#fff1f0' };
        } else {
            style = { ...style, border: '2px solid #faad14', background: '#fffbe6' };
        }
    } else if (currentUser) {
        if (lot.last_bid_user_id === currentUser.id) {
            style = { ...style, border: '2px solid #1890ff', background: '#e6f7ff' };
        } else if (lot.last_bid_user_id) {
            style = { ...style, border: '2px solid #faad14', background: '#fffbe6' };
        }
    }

    // Add pending bid style
    if (lot.pending_bid_user_id) {
        style = {
            ...style,
            border: '2px dashed #faad14',
            background: 'rgba(250, 173, 20, 0.1)',
            animation: 'highlight 1s ease-in-out'
        };
    }
    
    if (isSelected) {
        style = { ...style, boxShadow: '0 0 0 2px #1890ff', border: '2px solid #1890ff' };
    }
    
    // Add new bid animation
    if (newBidLots && newBidLots.has(lot.id)) {
        style = { 
            ...style, 
            animation: 'newBidHighlight 2s ease-in-out infinite',
            border: '2px solid #1890ff'
        };
    }
    
    if (!isAvailable) {
        style = { ...style, opacity: 0.5 };
    }
    
    return style;
};

